import type { CrossSellingServiceResponse } from 'cross-selling/types'
import { getStorageStrategy } from 'storage'
import type { Storage } from 'storage/constants'
import type { StorageClass } from 'storage/domain/Storage'

import { CROSS_SELLING_MAX_VIEW_ITEMS } from '../../constants/constants'

export const LAST_VIEWED_CACHE_KEY = 'LASTVIEWED_CACHE'
export const LASTVIEWED_CACHE_KEY = 'PRODUCTS'

const MICROSECONDS_PER_MILLISECOND = 1000

let lastViewedCache: StorageClass

interface GetLastViewedProductsProps {
	productId?: string
	colorId?: string
	storagePreference?: Storage
}

export interface UpdateLastViewedProductsCache {
	productId: string
	selectedColorId: string
	selectedLookId: string
	storagePreference?: Storage
}

export type LastViewedCacheProduct = CrossSellingServiceResponse & {
	timestamp: number
}

export const getLastViewedProducts = async ({
	productId,
	colorId,
	storagePreference,
}: GetLastViewedProductsProps): Promise<LastViewedCacheProduct[]> => {
	const lastViewedProducts = await getLastViewedCacheProducts(storagePreference)

	const filteredProducts = lastViewedProducts.filter(
		(lastViewedProduct) =>
			lastViewedProduct.productId !== productId ||
			lastViewedProduct.colorId !== colorId
	)

	// We have to limit the products just in case we are currenty viewing a saved one
	return filteredProducts.slice(0, CROSS_SELLING_MAX_VIEW_ITEMS)
}

export const updateLastViewedProductsCache = async ({
	productId,
	selectedColorId,
	selectedLookId,
	storagePreference,
}: UpdateLastViewedProductsCache): Promise<void> => {
	const productToAdd: LastViewedCacheProduct = {
		productId,
		colorId: selectedColorId,
		lookId: selectedLookId,
		timestamp: Date.now() * MICROSECONDS_PER_MILLISECOND,
	}

	const lastViewedProducts = await getLastViewedCacheProducts(storagePreference)

	// Cache does not exist. Add to cache the product as the only one
	if (!lastViewedProducts.length) {
		return addProductToLastViewedCache([productToAdd])
	}

	const alreadyViewedProduct = lastViewedProducts.find(
		(lastViewedProduct) =>
			lastViewedProduct.productId === productId &&
			lastViewedProduct.colorId === selectedColorId
	)

	// Updating the current product timestamp
	if (alreadyViewedProduct) {
		alreadyViewedProduct.timestamp = Date.now() * MICROSECONDS_PER_MILLISECOND
		// Sort with the new timestamp
		const sortedLastViewedProducts = [...lastViewedProducts].sort(
			(a, b) => b.timestamp - a.timestamp
		)

		return addProductToLastViewedCache(
			sortedLastViewedProducts,
			storagePreference
		)
	}

	// Pushing a new product and pop if necessary
	lastViewedProducts.push(productToAdd)

	const sortedNewLastViewedProducts = [...lastViewedProducts].sort(
		(a, b) => b.timestamp - a.timestamp
	)

	if (sortedNewLastViewedProducts.length > CROSS_SELLING_MAX_VIEW_ITEMS + 1) {
		sortedNewLastViewedProducts.pop()
	}

	return addProductToLastViewedCache(
		sortedNewLastViewedProducts,
		storagePreference
	)
}

export const addProductToLastViewedCache = async (
	lastViewedProducts: LastViewedCacheProduct[],
	storagePreference?: Storage
): Promise<void> => {
	getLastViewedCache(storagePreference).addKeyToCache<LastViewedCacheProduct[]>(
		LASTVIEWED_CACHE_KEY,
		lastViewedProducts
	)
}

export const getLastViewedCacheProducts = async (
	storagePreference?: Storage
): Promise<LastViewedCacheProduct[]> => {
	const lastViewedProducts =
		await getLastViewedCache(storagePreference).getKeyFromCache<
			LastViewedCacheProduct[]
		>(LASTVIEWED_CACHE_KEY)

	return lastViewedProducts || []
}

export const getLastViewedCache = (
	storagePreference?: Storage
): StorageClass => {
	if (!lastViewedCache) {
		lastViewedCache = getStorageStrategy({
			cacheKey: LAST_VIEWED_CACHE_KEY,
			isTokenCache: false,
			storagePreference,
		})
	}
	return lastViewedCache
}
